import { notEmpty, Numeric } from '@ezchops/helpers';
import { chunk, omit, range } from 'lodash';
import { isChordTuplet } from '../sheet';
export function patternToSheet(pattern, leadIn = 0, leadOut = 0, repeats) {
    const leadInSegment = leadIn == 0 ? null : generateEmptySegment(pattern.signature, leadIn);
    const patternSegment = { ...patternToSheetSegment(pattern), repeats };
    const leadOutSegment = leadOut == 0 ? null : generateEmptySegment(pattern.signature, leadOut);
    const segments = [leadInSegment, patternSegment, leadOutSegment,].filter(notEmpty);
    return { segments };
}
function generateEmptySegment(signature, measures) {
    if (measures == 0) {
        throw new Error('Cannot generate empty segment with 0 measures');
    }
    return {
        signature,
        measures: range(0, measures).map(() => ({
            notes: [
                { duration: [signature.numerator, signature.denominator], notes: [] }
            ],
        })),
        repeats: 0
    };
}
export function patternToSheetSegment(pattern) {
    const measures = patternToGroupedMeasureNotes(pattern);
    return {
        signature: omit(pattern.signature, 'tickNote'),
        measures: measures.map(beats => ({ notes: beats.flat() })),
        repeats: 0
    };
}
export function patternToGroupedMeasureNotes(pattern) {
    const tickNote = pattern.signature.tickNote ?? pattern.signature.denominator;
    const beatTicks = tickNote / pattern.signature.denominator;
    const numberOfTicks = pattern.measures * pattern.signature.numerator * beatTicks;
    const ticks = groupHitsByTick(pattern.hits, 0, numberOfTicks, tickNote).map(tickToNote);
    const measures = chunk(ticks, numberOfTicks / pattern.measures).map(measure => chunk(measure, beatTicks));
    return measures.map(beats => beats.map(b => reduceToCommonDenominator(b, beatTicks)));
}
function reduceToCommonDenominator(ticks, tickNote) {
    return reduce(ticks, tickNote, Numeric.primeFactors(tickNote));
    function reduce(chords, denominator, primeFactors) {
        if (primeFactors.length == 0) {
            return chords;
        }
        const emptyTicksFactor = primeFactors.filter(([prime,]) => chords.every((t, i) => isRest(t) || i % prime == 0))?.[0];
        if (emptyTicksFactor != null) {
            // drop rests
            const factor = emptyTicksFactor[0];
            const foldedChords = chunk(chords, factor).map(chords => chords[0]).map((chord) => ({
                ...chord, duration: [chord.duration[0], chord.duration[1] / factor]
            }));
            return reduce(foldedChords, denominator / factor, dropFactor(primeFactors, factor));
        }
        else {
            if (denominator % 2 == 0) {
                const slices = chunk(chords, chords.length / 2);
                const reducedSlices = slices.map(slice => reduce(slice, denominator / 2, dropFactor(primeFactors, 2)));
                return reducedSlices.flat();
            }
            else if (chords.length == denominator) {
                if (chords.slice(1).every(isRest)) {
                    return [{
                            ...chords[0],
                            duration: [chords[0].duration[0], chords[0].duration[1] / denominator]
                        }];
                }
                else {
                    return [{
                            chords: chords.map(t => t.notes),
                            duration: [2 * chords[0].duration[0], 2 * chords[0].duration[1] / denominator]
                        }];
                }
            }
            else {
                const slices = chunk(chords, denominator);
                const reducedSlices = slices.map(slice => reduce(slice, denominator / 2, dropFactor(primeFactors, 2)));
                return reducedSlices.flat();
            }
        }
    }
}
function isRest(chord) {
    return !isChordTuplet(chord) && chord.notes.length == 0;
}
function tickToNote(t) {
    if (t.hits.length == 0) {
        return { duration: t.duration, notes: [] };
    }
    else if (t.hits.length == 1 && t.hits[0].note == null) {
        return { duration: t.duration, notes: [{}] };
    }
    else {
        return {
            duration: t.duration,
            notes: t.hits
                .filter(h => h.note != null)
                .map(({ note, mode, dynamics, sticking, lag }) => ({
                pitch: note,
                ...mode != null ? { mode } : {},
                ...dynamics != null ? { dynamics } : {},
                ...sticking != null ? { sticking } : {},
                ...lag != null ? { lag } : {}
            }))
        };
    }
}
function groupHitsByTick(hits, firstTick, numberOfTicks, tickNote) {
    const ticks = range(firstTick, firstTick + numberOfTicks).map(() => ({ duration: [1, tickNote], hits: [] }));
    hits.forEach((hit) => {
        ticks[hit.tick - firstTick]?.hits.push(hit);
    });
    return ticks;
}
function dropFactor(factors, p) {
    return factors.map(([sp, sn]) => (sp == p ? [sp, sn - 1] : [sp, sn]))
        .filter(([, n]) => n > 0);
}
//# sourceMappingURL=patternToSheet.js.map